import React from "react";
import { Text, View, StyleSheet, Font } from "@react-pdf/renderer";
import ArialRegular from "../../generatePdf/components/Arial/ARIAL.TTF";
import ArialBold from "../../generatePdf/components/Arial/ARIALBD.TTF";
import ArialItalic from "../../generatePdf/components/Arial/ARIALI.TTF";

Font.register({
  family: "Arial",
  format: "truetype",
  src: ArialRegular,
});

Font.register({
  family: "ArialBold",
  format: "truetype",
  src: ArialBold,
});

Font.register({
  family: "ArialItalic",
  format: "truetype",
  src: ArialItalic,
});

const styles = StyleSheet.create({
  page: {
    paddingTop: "0.5in",
    paddingLeft: "0.35in",
    paddingRight: "0.35in",
    paddingBottom: "0.3in",
    fontFamily: "Arial",
    fontSize: 8,
  },
  formNo: {
    position: "absolute",
    top: "0.4in",
    right: "0.35in",
    textAlign: "right",
    fontSize: 8,
  },
  header: {
    marginTop: 0,
    textAlign: "center",
    alignItems: "center",
  },
  upText: {
    fontSize: 10,
    margin: 0,
  },
  ouText: {
    fontSize: 13,
    fontFamily: "ArialBold",
  },
  headerLine: {
    borderTop: 1,
    borderColor: "black",
    width: 247, // ~330px
    marginTop: 3,
  },
  locationText: {
    fontSize: 9,
    marginTop: 3,
  },
  title: {
    fontSize: 13,
    fontFamily: "ArialBold",
    marginTop: 10,
    letterSpacing: 1,
  },
  row: {
    flexDirection: "row",
    marginTop: 5,
  },
  fieldLabel: {
    paddingRight: 5,
  },
  underline: {
    borderBottom: 1,
    borderColor: "black",
    flex: 1,
    minHeight: 13, // Match PHP min-height: 13px
    paddingBottom: 1,
  },
  underlineText: {
    borderBottom: 1,
    borderColor: "black",
    width: "100%",
    minHeight: 13,
    marginTop: 2,
  },
  table: {
    width: "100%",
    marginTop: 15,
  },
  borderTable: {
    border: 1,
    borderColor: "black",
    marginTop: 5,
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: 1,
    borderColor: "black",
  },
  tableCell: {
    padding: 2,
    borderRight: 1,
    borderColor: "black",
    minHeight: 19, // Match PHP 19px
    justifyContent: "center",
  },
  approvalContainer: {
    marginTop: 10,
    marginBottom: 2,
    width: "85%",
    alignSelf: "center",
    flexDirection: "row",
  },
  approvalCol: {
    width: "50%",
  },
  signatureBlock: {
    width: 170, // ~220px
    marginTop: 20,
    textAlign: "center",
    alignItems: "center",
  },
  nameArea: {
    height: 14,
    borderBottom: 1,
    borderColor: "black",
    justifyContent: "center",
    textAlign: "center",
    width: "100%",
    marginBottom: 2,
  },
  bold: {
    fontFamily: "ArialBold",
  },
  italic: {
    fontFamily: "ArialItalic",
    fontSize: 7.5,
  },
  sectionTitle: {
    fontFamily: "ArialBold",
    fontSize: 10,
    textAlign: "center",
    marginTop: 6,
  },
  sectionSub: {
    fontSize: 8,
    textAlign: "center",
    marginBottom: 2,
  },
});



const TripTicketFormat = ({ contents }) => {
  const trip = contents;
  const plateNo = trip.vehicle?.plate_number || trip.custom_vehicle_plate || "";
  const dateOfTravel = trip.trip_date ? new Date(trip.trip_date).toLocaleDateString("en-US", { year: 'numeric', month: 'long', day: 'numeric' }) : "";
  const requestedBy = trip.organizing_unit || "";
  const driver = trip.driver?.driver_name || trip.custom_driver_name || "";
  const destination = trip.dropoff_location || "";
  const departureRaw = trip.pickup_time || "";
  const departure = departureRaw ? (() => {
    const [h, m] = departureRaw.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${hour % 12 || 12}:${m} ${ampm}`;
  })() : "";
  const purpose = trip.trip_purpose || "";

  // Passengers
  const approvedPassengers = (trip.passengers || []).filter(p => ["Approved", "Approved by AO", "Completed Trip"].includes(p.status || p.pivot?.request_status || "Approved"));
  const passengerNames = approvedPassengers.map(p => 
    p.name || 
    p.pivot?.external_passenger_name || 
    p.external_passenger_name || 
    (p.first_name ? `${p.first_name} ${p.last_name || ''}`.trim() : "") ||
    (p.employee?.first_name ? `${p.employee.first_name} ${p.employee.last_name || ''}`.trim() : "")
  ).filter(name => name);

  // Approver
  const aoName = contents.aoName || "";

  return (
    <View style={styles.page}>
      <View style={styles.formNo}>
        <Text>UPOU-GS/AO Form No. 2</Text>
        <Text>Revised 2013</Text>
      </View>

      <View style={styles.header}>
        <Text style={styles.upText}>University of the Philippines</Text>
        <Text style={styles.ouText}>OPEN UNIVERSITY</Text>
        <View style={styles.headerLine} />
        <Text style={styles.locationText}>Los Baños, 4031 Laguna</Text>
        <Text style={styles.title}>DRIVER'S TRIP TICKET</Text>
      </View>

      <View style={{ ...styles.row, marginTop: 10 }}>
        <View style={{ flexDirection: "row", width: "25%" }}>
          <Text style={styles.fieldLabel}>Plate No.:</Text>
          <View style={styles.underline}><Text>{plateNo}</Text></View>
        </View>
        <View style={{ flexDirection: "row", width: "40%", paddingLeft: 10 }}>
          <Text style={styles.fieldLabel}>Transportation Order No.:</Text>
          <View style={styles.underline}><Text>{" "}</Text></View>
        </View>
        <View style={{ flexDirection: "row", width: "35%", paddingLeft: 10 }}>
          <Text style={styles.fieldLabel}>Date of Travel:</Text>
          <View style={styles.underline}><Text>{dateOfTravel}</Text></View>
        </View>
      </View>

      <View style={styles.row}>
        <View style={{ flexDirection: "row", width: "30%" }}>
          <Text style={styles.fieldLabel}>Requested by:</Text>
          <View style={styles.underline}><Text>{requestedBy}</Text></View>
        </View>
        <View style={{ flexDirection: "row", width: "45%", paddingLeft: 10 }}>
          <Text style={styles.fieldLabel}>Assigned Driver:</Text>
          <View style={styles.underline}><Text>{driver}</Text></View>
        </View>
        <View style={{ flexDirection: "row", width: "25%", paddingLeft: 10 }}>
          <Text style={styles.fieldLabel}>Departure:</Text>
          <View style={styles.underline}><Text>{departure}</Text></View>
        </View>
      </View>

      <View style={{ ...styles.row, marginTop: 5 }}>
        <View style={{ width: "45%" }}>
          <View style={{ flexDirection: "row" }}>
            <Text style={styles.fieldLabel}>Head of Party:</Text>
            <View style={styles.underline}><Text>{" "}</Text></View>
          </View>
          <Text style={{ marginTop: 8, marginBottom: 2 }}>Passenger/s:</Text>
          {Array.from({ length: 8 }).map((_, i) => (
            <View key={i} style={styles.underlineText}>
               <Text>{passengerNames[i] || " "}</Text>
            </View>
          ))}
        </View>

        <View style={{ width: "55%", paddingLeft: 15 }}>
          <View style={{ flexDirection: "row" }}>
            <Text style={styles.fieldLabel}>Destination(s):</Text>
            <View style={styles.underline}><Text>{destination}</Text></View>
          </View>
          {Array.from({ length: 3 }).map((_, i) => (
            <View key={i} style={styles.underlineText}><Text>{" "}</Text></View>
          ))}

          <View style={{ flexDirection: "row", marginTop: 8 }}>
            <Text style={styles.fieldLabel}>Purpose(s):</Text>
            <View style={styles.underline}><Text>{purpose}</Text></View>
          </View>
          {Array.from({ length: 4 }).map((_, i) => (
            <View key={i} style={styles.underlineText}><Text>{" "}</Text></View>
          ))}
        </View>
      </View>

      <View style={styles.approvalContainer}>
        <View style={styles.approvalCol}>
          <Text>Approved for Use of Vehicle:</Text>
          <View style={styles.signatureBlock}>
            <View style={styles.nameArea}>
              <Text style={{ fontFamily: "ArialBold", textTransform: "uppercase" }}>{aoName || " "}</Text>
            </View>
            <Text style={{ fontSize: 8 }}>Administrative Officer</Text>
          </View>
        </View>
        <View style={styles.approvalCol}>
          <Text>Approved for Funds:</Text>
          <View style={styles.signatureBlock}>
            <View style={styles.nameArea}>
              <Text>{" "}</Text>
            </View>
            <Text style={{ fontSize: 8 }}>Immediate Supervisor</Text>
          </View>
        </View>
      </View>

      <View style={styles.borderTable}>
        <View style={styles.tableRow}>
          <View style={{ ...styles.tableCell, width: "100pt", borderRight: 0 }}><Text>Mileage - Out :</Text></View>
          <View style={{ ...styles.tableCell, width: "100pt", borderRight: 0 }} />
          <View style={{ ...styles.tableCell, width: "40pt", borderRight: 0, textAlign: "center" }}><Text>In</Text></View>
          <View style={{ ...styles.tableCell, width: "100pt", borderRight: 1 }} />
          <View style={{ ...styles.tableCell, width: "140pt", borderRight: 0 }}><Text>Distance Traveled:</Text></View>
          <View style={{ ...styles.tableCell, flex: 1, borderRight: 0 }} />
        </View>
        <View style={{ ...styles.tableRow, borderBottom: 0 }}>
          <View style={{ ...styles.tableCell, width: "100pt", borderRight: 0 }}><Text>Time - Out :</Text></View>
          <View style={{ ...styles.tableCell, width: "100pt", borderRight: 0 }} />
          <View style={{ ...styles.tableCell, width: "40pt", borderRight: 0, textAlign: "center" }}><Text>In</Text></View>
          <View style={{ ...styles.tableCell, width: "100pt", borderRight: 1 }} />
          <View style={{ ...styles.tableCell, width: "140pt", borderRight: 0 }}><Text>Hours Used :</Text></View>
          <View style={{ ...styles.tableCell, flex: 1, borderRight: 0 }} />
        </View>
      </View>

      <Text style={styles.sectionTitle}>GASOLINE WITHDRAWAL</Text>
      <Text style={styles.sectionSub}>(for Exclusive Use of Government Registered Vehicle)</Text>

      <View style={styles.borderTable}>
        <View style={styles.tableRow}>
          <View style={{ ...styles.tableCell, width: "155pt", borderRight: 0 }}><Text>Quantity to be Issued (liters):</Text></View>
          <View style={{ ...styles.tableCell, width: "100pt", borderRight: 1 }} />
          <View style={{ ...styles.tableCell, width: "125pt", borderRight: 0 }}><Text>Actual Load (liters):</Text></View>
          <View style={{ ...styles.tableCell, width: "100pt", borderRight: 1 }} />
          <View style={{ ...styles.tableCell, width: "95pt", borderRight: 0 }}><Text>Amount (PhP):</Text></View>
          <View style={{ ...styles.tableCell, flex: 1, borderRight: 0 }} />
        </View>
        <View style={{ ...styles.tableRow, borderBottom: 0 }}>
          <View style={{ ...styles.tableCell, width: "20%", borderRight: 0, borderBottom: 0, minHeight: 38 }}><Text>Office(s) to be served:</Text></View>
          <View style={{ ...styles.tableCell, width: "30%", borderRight: 1, borderBottom: 0 }} />
          <View style={{ ...styles.tableCell, width: "20%", borderRight: 0, borderBottom: 0 }}><Text>Authorized by:</Text></View>
          <View style={{ ...styles.tableCell, flex: 1, borderRight: 0, borderBottom: 0 }} />
        </View>
      </View>

      <Text style={{ ...styles.sectionTitle, fontSize: 9, marginTop: 6 }}>e-PASS/Tollgate Monitoring/Control</Text>
      <View style={styles.borderTable}>
        <View style={{ ...styles.tableRow, backgroundColor: "#f9f9f9" }}>
           <View style={{ ...styles.tableCell, width: "35%", textAlign: "center" }}><Text style={styles.bold}>Entry Point(s)</Text></View>
           <View style={{ ...styles.tableCell, width: "35%", textAlign: "center" }}><Text style={styles.bold}>Exit Point(s)</Text></View>
           <View style={{ ...styles.tableCell, flex: 1, borderRight: 0, textAlign: "center" }}><Text style={styles.bold}>Amount</Text></View>
        </View>
        {Array.from({ length: 4 }).map((_, i) => (
           <View key={i} style={{ ...styles.tableRow, borderBottom: i === 3 ? 0 : 1 }}>
              <View style={{ ...styles.tableCell, width: "35%" }} />
              <View style={{ ...styles.tableCell, width: "35%" }} />
              <View style={{ ...styles.tableCell, flex: 1, borderRight: 0 }} />
           </View>
        ))}
      </View>

      <View style={{ ...styles.borderTable, marginTop: 15, flexDirection: "row" }}>
        <View style={{ width: "50%", padding: 5, borderRight: 1 }}>
           <Text style={{ fontSize: 8, height: 25 }}>I hereby certify to the correctness of the above statement of record of travel</Text>
           <View style={{ ...styles.signatureBlock, alignSelf: "center", marginTop: 15 }}>
              <View style={styles.nameArea}>
                 <Text style={{ fontFamily: "ArialBold", textTransform: "uppercase" }}>{driver || " "}</Text>
              </View>
              <Text style={{ ...styles.bold, fontSize: 8.5 }}>Driver</Text>
              <Text style={styles.italic}>(Signature over Printed Name)</Text>
           </View>
        </View>
        <View style={{ width: "50%", padding: 5 }}>
           <Text style={{ fontSize: 8, height: 25 }}>I hereby certify that I used this vehicle on official business as stated above</Text>
           <View style={{ ...styles.signatureBlock, alignSelf: "center", marginTop: 15 }}>
              <View style={styles.nameArea}>
                 <Text>{" "}</Text>
              </View>
              <Text style={{ ...styles.bold, fontSize: 8.5 }}>Head of Party</Text>
              <Text style={styles.italic}>(Signature over Printed Name)</Text>
           </View>
        </View>
      </View>
    </View>
  );
};

export default TripTicketFormat;
